import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState } from '@/tools/tool.utilities';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { FrontendDuration } from '@/services/systemConfiguration.types';
import type { BaseToolPersistedState, ToolRehydratePayload } from '@/tools/tool.types';

export const DEFAULT_DURATION: FrontendDuration = { value: 0, units: 'min' };

export type ScatterConditionState = BaseToolPersistedState & {
  isBounding: boolean;
  isCleansing: boolean;
  minDuration: FrontendDuration;
  mergeDuration: FrontendDuration;
};

export type ScatterConditionDehydratedState = ScatterConditionState;

export type ScatterConditionActions = {
  setSearchName: (name: string) => void;
  setBounding: (isBounding: boolean) => void;
  setCleansing: (isCleansing: boolean) => void;
  setMinDuration: (minDuration: FrontendDuration) => void;
  setMergeDuration: (mergeDuration: FrontendDuration) => void;
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  reset: ResettableStoreState<ScatterConditionState>;
};

const initialState: ScatterConditionState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  isBounding: false,
  isCleansing: false,
  minDuration: DEFAULT_DURATION,
  mergeDuration: DEFAULT_DURATION,
});

export const dehydrateScatterConditionState = (state: ScatterConditionState): ScatterConditionDehydratedState => state;

const scatterConditionConfigParamsSelector = createSelector([(state: ScatterConditionState) => state], (state) => ({
  type: TREND_TOOLS.SCATTER_CONDITION,
  maximumDuration: state.maximumDuration,
  advancedParametersCollapsed: state.advancedParametersCollapsed,
  configVersion: state.configVersion,
  isBounding: state.isBounding,
  isCleansing: state.isCleansing,
  minDuration: state.minDuration,
  mergeDuration: state.mergeDuration,
}));

const scatterConditionActionsFactory: ActionsFactory<ScatterConditionState, ScatterConditionActions> = (
  set,
  _get,
  resetState,
) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setBounding: (isBounding) =>
    set((draft) => {
      draft.isBounding = isBounding;
    }),

  setCleansing: (isCleansing) =>
    set((draft) => {
      draft.isCleansing = isCleansing;
    }),

  setMinDuration: (minDuration) =>
    set((draft) => {
      draft.minDuration = minDuration;
    }),

  setMergeDuration: (mergeDuration) =>
    set((draft) => {
      draft.mergeDuration = mergeDuration;
    }),

  rehydrateForEdit: (payload) =>
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        currentAdvancedParametersCollapsed: initialState.advancedParametersCollapsed,
      });

      Object.assign(draft, initialState, rehydrationState.config, {
        originalParameters: rehydrationState.originalParameters,
      });
    }),

  reset: resetState,
});

const STORE_NAME = 'ScatterCondition';

const [ScatterConditionStoreProvider, useScatterConditionStoreInstance, useScatterConditionStoreSelector] = createStore(
  STORE_NAME,
  initialState,
  scatterConditionActionsFactory,
);

export { ScatterConditionStoreProvider };

export const useScatterConditionId = () => useScatterConditionStoreSelector((state) => state.id);
export const useScatterConditionName = () => useScatterConditionStoreSelector((state) => state.name);
export const useScatterConditionBounding = () => useScatterConditionStoreSelector((state) => state.isBounding);
export const useScatterConditionCleansing = () => useScatterConditionStoreSelector((state) => state.isCleansing);
export const useScatterConditionMinDuration = () => useScatterConditionStoreSelector((state) => state.minDuration);
export const useScatterConditionMergeDuration = () => useScatterConditionStoreSelector((state) => state.mergeDuration);
export const useScatterConditionConfigParams = () =>
  useScatterConditionStoreSelector(scatterConditionConfigParamsSelector);
export const useScatterConditionActions = () => useScatterConditionStoreInstance().actions;

export function createTestScatterConditionStore(overrides?: Partial<ScatterConditionState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestScatterConditionStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, scatterConditionActionsFactory);
}

/** @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. */
export function legacyCreateScatterConditionStore() {
  return createStoreInstance<ScatterConditionState, ScatterConditionActions>(
    initialState,
    scatterConditionActionsFactory,
    { storeName: STORE_NAME },
  );
}

export const usePersistScatterConditionStore = () => {
  const register = useRegisterPersistedStore();
  const store = useScatterConditionStoreInstance();

  useEffect(
    () =>
      register({
        storeName: 'sqScatterConditionStore',
        persistenceLevel: 'WORKSHEET',
        store,
        dehydrate: dehydrateScatterConditionState,
      }),
    [register, store],
  );
};
